// Portfolio copy is based on the supplied resumes and existing project records.
export const assetUrl = file => `${process.env.PUBLIC_URL || ''}/${file}`;

export const projects = [
  {
    number: '01',
    title: '5-stage RISC-V processor',
    discipline: 'RTL DESIGN & VERIFICATION',
    categories: ['hardware'],
    featured: true,
    description: 'Synthesizable SystemVerilog RTL with forwarding, hazard detection, pipeline stalls, and control-hazard flushing. Verified with self-checking testbenches, assertions, and waveform analysis across arithmetic, branch, and memory operations.',
    tags: ['SystemVerilog', 'ModelSim', 'RISC-V', 'RTL verification'],
    links: [{ label: 'View source', href: 'https://github.com/Rpatel23648/ECE-3058/tree/main/Lab%202%20-%20Pipelining' }, { label: 'Processor diagram', href: assetUrl('Screenshot 2025-08-21 105825.png') }]
  },
  {
    number: '02',
    title: 'Memory-mapped PWM peripheral',
    discipline: 'FPGA & PERIPHERALS',
    categories: ['hardware'],
    description: 'A VHDL peripheral for the Intel DE10 FPGA, with address-decoded control registers and configurable LED duty cycles. Integrated through memory-mapped I/O and validated through simulation, synthesis, on-board testing, and timing analysis.',
    tags: ['VHDL', 'Intel Quartus', 'FPGA', 'Memory-mapped I/O'],
    links: [],
    detail: 'RTL design → simulation → on-board verification'
  },
  {
    number: '03',
    title: 'Embedded Snake on ARM',
    discipline: 'EMBEDDED SOFTWARE',
    categories: ['hardware', 'software'],
    description: 'A C++ game deployed on an ARM microcontroller with a custom-wired display and input controls. Low-level display and input drivers connect the hardware interface to responsive, bare-metal gameplay.',
    tags: ['C/C++', 'ARM', 'Bare-metal', 'Peripheral drivers'],
    links: [{ label: 'Watch hardware demo', href: assetUrl('IMG_0049.mov') }]
  },
  {
    number: '04',
    title: 'Quick & Quality ordering platform',
    discipline: 'FULL-STACK DEVELOPMENT',
    categories: ['software'],
    description: 'An ordering and management platform for a local deli. Customer ordering, staff order tracking with text notifications, and an owner dashboard for sales reporting and menu pricing, built with React, Node.js, and MongoDB.',
    tags: ['React', 'Node.js', 'MongoDB', 'Full-stack'],
    links: [{ label: 'Visit website', href: 'https://quickandquality.net' }]
  },
  {
    number: '05',
    title: 'Cache & virtual memory simulators',
    discipline: 'COMPUTER ARCHITECTURE',
    categories: ['hardware', 'software'],
    description: 'C implementations exploring cache organization and virtual memory behavior. Models configurable cache sizes and associativity with LRU replacement, alongside page tables, address translation, and write-back page replacement.',
    tags: ['C', 'Memory systems', 'LRU caching', 'Make'],
    links: [{ label: 'Cache source', href: 'https://github.com/Rpatel23648/ECE-3058/tree/main/Lab%203%20-%20Caches' }, { label: 'Virtual memory source', href: 'https://github.com/Rpatel23648/ECE-3058/tree/main/Lab%204%20-%20Virtual%20Memory' }]
  },
  {
    number: '06',
    title: 'Multithreaded CPU scheduler',
    discipline: 'SYSTEMS PROGRAMMING',
    categories: ['software'],
    description: 'A multithreaded simulation of CPU scheduling in C. Implements First In, First Out and Round Robin scheduling to explore how scheduling policies coordinate concurrent workloads.',
    tags: ['C', 'Multithreading', 'CPU scheduling', 'Make'],
    links: [{ label: 'View source', href: 'https://github.com/Rpatel23648/ECE-3058/tree/main/Lab%205%20-%20Multithreading/assignment' }]
  },
  {
    number: '07',
    title: 'AI course assistant',
    discipline: 'APPLIED MACHINE LEARNING',
    categories: ['software'],
    description: 'A GPT-2 course assistant trained on Georgia Tech AI course material. Investigated tokenization, learning rates, and model architecture, with results documented in a technical report and research poster.',
    tags: ['Python', 'TensorFlow', 'GPT-2', 'Model evaluation'],
    links: [{ label: 'Read report', href: assetUrl('Report_-_Romil_Patel.pdf') }, { label: 'Research poster', href: assetUrl('Poster_Template_2.pdf') }]
  }
];

export const experience = [
  {
    title: 'Control Systems Software Member',
    organization: 'Georgia Tech SuperSonics · Control Systems Subteam',
    date: 'Aug 2026 – Present',
    location: 'Atlanta, GA',
    points: ['Translating MATLAB navigation algorithms, control logic, and lookup tables into aircraft control software.', 'Integrating with open-source aircraft software and working with the GNC team to validate behavior against the original models.']
  },
  {
    title: 'Software Engineer Intern',
    organization: 'Georgia Tech Research Institute · Electronic Systems Lab',
    date: 'Jan 2026 – May 2026',
    location: 'Smyrna, GA',
    points: ['Architected a modular C++ IPC library using sockets and message queues for distributed radar test systems.', 'Developed flight-path simulation algorithms and integrated radar software with hardware, data-acquisition, and external system interfaces.', 'Rebuilt GitLab CI/CD pipelines and automated Windows and RHEL package creation.'],
    metrics: [{ value: '25%', label: 'lower communication latency' }, { value: '50%', label: 'reduction in build time' }]
  },
  {
    title: 'Information Technology Specialist',
    organization: 'Georgia Tech · Technology Services Organization',
    date: 'Aug 2024 – Dec 2025',
    location: 'Atlanta, GA',
    points: ['Developed and maintained software deployment workflows for 4,000+ managed devices.', 'Automated diagnostics and ticket triage, reducing average issue-resolution time by approximately 25%.']
  }
];

export const skillGroups = [
  { title: 'Silicon & digital design', items: ['SystemVerilog', 'Verilog', 'VHDL', 'RTL verification', 'FPGA design', 'Synthesis & timing analysis', 'ModelSim', 'Intel Quartus', 'Cadence', 'Synopsys'] },
  { title: 'Systems & embedded software', items: ['C', 'C++', 'Python', 'CUDA', 'RISC-V assembly', 'Linux', 'ARM', 'Bare-metal programming', 'IPC & sockets', 'Multithreading'] },
  { title: 'Tools & development', items: ['Git', 'GitLab CI/CD', 'CMake', 'Make', 'Docker', 'LLVM/Clang', 'React', 'Node.js', 'MongoDB'] }
];

export const resumes = [
  { title: 'Silicon & hardware engineering', shortName: 'hardware', category: 'DIGITAL DESIGN / FPGA / VERIFICATION', description: 'RTL design, processor architecture, FPGA development, and hardware–software integration.', file: 'resumes/Patel-Romil-Resume-Hardware.pdf' },
  { title: 'Software engineering', shortName: 'software', category: 'C++ / SYSTEMS / EMBEDDED', description: 'Systems programming, embedded development, distributed communication, and engineering tools.', file: 'resumes/Patel-Romil-Software-Resume.pdf' }
];
